import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { handleBookDemoClick } from './navbar.utils';

const MobileNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Handle ScrollSmoother based on menu state
  useEffect(() => {
    const scrollSmoother = ScrollSmoother.get();
    if (!scrollSmoother) return;

    if (isMenuOpen) {
      // Pause immediately when opening
      scrollSmoother.paused(true);
    } else {
      // Resume after the CSS transition completes
      const resumeTimer = setTimeout(() => {
        scrollSmoother.paused(false);
      }, 200);

      return () => clearTimeout(resumeTimer);
    }
  }, [isMenuOpen]);

  return (
    <div className="md:hidden">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 w-full backdrop-blur-xl bg-dark-background/60 z-50 flex justify-between items-center px-4 py-3">
        {/* Logo */}
        <Link to="/" className="text-xl font-medium py-0 md:py-5">
          <img src="/images/aim-logo_light.svg" className="h-6" />
        </Link>

        {/* Hamburger Menu Icon */}
        <button className="focus:outline-none" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <div className="relative w-8 h-8">
            <span
              className={`block absolute h-0.5 w-full bg-white transform transition duration-300 ease-in-out ${
                isMenuOpen ? 'rotate-45 top-3.5' : 'top-2'
              }`}
            ></span>
            <span
              className={`block absolute h-0.5 w-full bg-white transition duration-300 ease-in-out ${
                isMenuOpen ? 'opacity-0' : 'top-4'
              }`}
            ></span>
            <span
              className={`block absolute h-0.5 w-full bg-white transform transition duration-300 ease-in-out ${
                isMenuOpen ? '-rotate-45 top-3.5' : 'top-6'
              }`}
            ></span>
          </div>
        </button>
      </div>

      {/* Overlay Menu */}
      {isMenuOpen && (
        <div className="fixed overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800 top-0 left-0 w-full h-full bg-black bg-opacity-95 text-white transform translate-y-0 transition-transform duration-300 ease-in-out z-40">
          <div className="pt-16 px-8">
            <ul className="space-y-6 mt-4">
              {/* AIM360 links */}
              <li>
                <div
                  className="flex justify-between items-center text-2xl cursor-pointer"
                  onClick={() => toggleDropdown(1)}
                >
                  <span>AIM360</span>
                  <span
                    className={`transform transition-transform duration-500 ease-in-out ${
                      activeDropdown === 1 ? 'rotate-180' : ''
                    }`}
                  >
                    <img className="h-3 px-1 h-6" src="/images/down-chevron-white.svg" />
                  </span>
                </div>
                <ul
                  className={`pl-2 mt-4 space-y-4 overflow-hidden transition-max-height duration-500 ease-in-out ${
                    activeDropdown === 1 ? 'max-h-screen' : 'max-h-0'
                  }`}
                >
                  <li className="pl-4 text-aim-accent">Data</li>
                  <li className="pl-8">
                    <Link to="/data/core-data/" onClick={() => setIsMenuOpen(false)}>
                      Core Data
                    </Link>
                  </li>
                  <li className="pl-8">
                    <Link to="/data/homemover/" onClick={() => setIsMenuOpen(false)}>
                      Homemover
                    </Link>
                  </li>
                  <li className="pl-4 text-aim-accent">Activation</li>
                  <li className="pl-8">
                    <Link to="/activation/direct-mail/" onClick={() => setIsMenuOpen(false)}>
                      Direct Mail
                    </Link>
                  </li>
                  <li className="pl-8">
                    <Link to="/activation/acquisition/" onClick={() => setIsMenuOpen(false)}>
                      Acquisition
                    </Link>
                  </li>
                  <li className="pl-8">
                    <Link to="/activation/retention/" onClick={() => setIsMenuOpen(false)}>
                      Retention
                    </Link>
                  </li>
                  <li className="pl-4 text-aim-accent">Insights</li>
                  <li className="pl-8">
                    <Link
                      to="/insights/live-campaign-reporting/"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Live Campaign Insights
                    </Link>
                  </li>
                  <li className="pl-8">
                    <Link to="/insights/roi-analytics/" onClick={() => setIsMenuOpen(false)}>
                      ROI Insights
                    </Link>
                  </li>
                  <li className="pl-8">
                    <Link to="/insights/segmentation/" onClick={() => setIsMenuOpen(false)}>
                      Segmentation
                    </Link>
                  </li>
                </ul>
              </li>

              {/* About Us links */}
              <li>
                <div
                  className="flex justify-between items-center text-2xl cursor-pointer"
                  onClick={() => toggleDropdown(3)}
                >
                  <span>About Us</span>
                  <span
                    className={`transform transition-transform duration-500 ease-in-out ${
                      activeDropdown === 3 ? 'rotate-180' : ''
                    }`}
                  >
                    <img className="h-3 px-1 h-6" src="/images/down-chevron-white.svg" />
                  </span>
                </div>
                <ul
                  className={`pl-2 mt-4 space-y-4 overflow-hidden transition-max-height duration-500 ease-in-out ${
                    activeDropdown === 3 ? 'max-h-screen' : 'max-h-0'
                  }`}
                >
                  <li className="pl-4 text-white">
                    <Link to="/about/about-us/" onClick={() => setIsMenuOpen(false)}>
                      About Us
                    </Link>
                  </li>
                  <li className="pl-4">
                    <Link to="/about/faq/" onClick={() => setIsMenuOpen(false)}>
                      FAQs
                    </Link>
                  </li>
                  <li className="pl-4">
                    <Link to="/about/why-dm/" onClick={() => setIsMenuOpen(false)}>
                      Why DM?
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Industries links */}
              <li>
                <div
                  className="flex justify-between items-center text-2xl cursor-pointer"
                  onClick={() => toggleDropdown(2)}
                >
                  <span>Industries</span>
                  <span
                    className={`transform transition-transform duration-500 ease-in-out ${
                      activeDropdown === 2 ? 'rotate-180' : ''
                    }`}
                  >
                    <img className="h-3 px-1 h-6" src="/images/down-chevron-white.svg" />
                  </span>
                </div>
                <ul
                  className={`pl-2 mt-2 space-y-2 overflow-hidden transition-max-height duration-500 ease-in-out ${
                    activeDropdown === 2 ? 'max-h-screen' : 'max-h-0'
                  }`}
                >
                  <li className="pl-4 text-white">
                    <Link
                      to="/industries/leisure-travel-cruises/"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Travel
                    </Link>
                  </li>
                  <li className="pl-4 text-white">
                    <Link to="/industries/property/" onClick={() => setIsMenuOpen(false)}>
                      Property
                    </Link>
                  </li>
                  <li className="pl-4 text-white">
                    <Link to="/industries/utilities/" onClick={() => setIsMenuOpen(false)}>
                      Utilities
                    </Link>
                  </li>
                  <li className="pl-4 text-white">
                    <Link to="/industries/broadband/" onClick={() => setIsMenuOpen(false)}>
                      Broadband
                    </Link>
                  </li>
                  <li className="pl-4 text-white">
                    <Link to="/industries/charities/" onClick={() => setIsMenuOpen(false)}>
                      Charity
                    </Link>
                  </li>
                  <li className="pl-4 text-white">
                    <Link to="/industries/senior-living/" onClick={() => setIsMenuOpen(false)}>
                      Senior Living
                    </Link>
                  </li>
                  <li className="pl-4 text-white">
                    <Link to="/industries/insurance/" onClick={() => setIsMenuOpen(false)}>
                      Insurance
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Book a Demo link */}
              <li>
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleBookDemoClick();
                  }}
                  className="text-2xl text-left"
                >
                  Book a Demo
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileNavbar;
